import { ArrowBack } from "@mui/icons-material";
import { Autocomplete, Box, Button, Card, CardContent, Checkbox, CircularProgress, Divider, FormControlLabel, IconButton, MenuItem, TextField, Typography } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs, { Dayjs } from "dayjs";
import { ReactNode, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import MainLayout from "../../../layouts/main/main_layout";
import Loading from "../../../components/loading/loading";
import OrgSelector from "../../../components/org_selector/org_selector";
import { useSelectedOrgStore } from "../../../store/use-selected-org-store";
import { useFeedback } from "../../../providers/feedback/feeedback";
import { queryClient } from "../../../providers/query/query";
import { black, purple } from "../../../theme/colors";
import { cms_banners } from "../../../routes/routes_path";
import { createBanner, getBannerById, getMediaById, getNewsList, updateBanner } from "../../../services/cms/cms";
import MediaPicker from "../components/media_picker/media_picker";
import TargetingFields, { buildTargetingPayload, defaultTargeting, targetingFromResponse, TargetingFormValues } from "../components/targeting_fields/targeting_fields";
import VideoPicker from "../components/video_picker/video_picker";
import { BANNER_TYPE_OPTIONS, Banner, BannerType, bannerTypeAspectRatio, bannerTypeLabel, CmsStatus, Media } from "../models/cms";

interface NewsOption {
  id: number;
  title: string;
}

interface BannerFormValues {
  title: string;
  status: CmsStatus;
  banner_type: BannerType;
  link_url: string;
  video_url: string;
  sort_order: number | null;
  active_date: string | null;
  expire_date: string | null;
  banner_image: Media | null;
  newsId: number | null;
  is_default: boolean;
  targeting: TargetingFormValues;
}

const defaultValues: BannerFormValues = {
  title: "",
  status: "draft",
  banner_type: "banners",
  link_url: "",
  video_url: "",
  sort_order: null,
  active_date: null,
  expire_date: null,
  banner_image: null,
  newsId: null,
  is_default: false,
  targeting: defaultTargeting,
};

function slugify(title: string): string {
  return title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

// A small bold, uppercase divider label — breaks the form into scannable
// groups instead of one long flat list of fields.
function SectionHeader({ children, mt = 4 }: { children: ReactNode; mt?: number }) {
  return (
    <Box mt={mt} mb={1.5}>
      <Typography variant="overline" fontWeight={700} color={purple} sx={{ letterSpacing: 0.6 }}>
        {children}
      </Typography>
      <Divider sx={{ mt: 0.5 }} />
    </Box>
  );
}

export default function AddEditBanner({ viewMode = false }: { viewMode?: boolean }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showSnackbar } = useFeedback();
  const isEdit = !!id;
  const { control, handleSubmit, watch, setValue, reset } = useForm<BannerFormValues>({ defaultValues });
  const { selectedOrgId } = useSelectedOrgStore();

  const { data: bannerDetails, isLoading: isBannerLoading } = useQuery<Banner | null>(
    ["cms-banner-details", id],
    async () => {
      const response = await getBannerById(id!);
      return response?.data?.data;
    },
    { enabled: !!id }
  );

  const { data: newsOptions } = useQuery(["cms-news-for-banner"], async () => {
    const response = await getNewsList({ page: 1, size: 100 });
    return (response?.data?.data?.list || []) as NewsOption[];
  });

  // getBannerById only returns the raw *_id integers, not a nested media
  // object, so the picker's existing-image preview needs a separate fetch.
  const { data: bannerImageMedia } = useQuery(
    ["cms-media", bannerDetails?.banner_image_id],
    async () => {
      const response = await getMediaById(bannerDetails!.banner_image_id!);
      return response?.data?.data as Media;
    },
    { enabled: !!bannerDetails?.banner_image_id }
  );

  useEffect(() => {
    if (bannerDetails) {
      reset({
        title: bannerDetails.title || "",
        status: bannerDetails.status || "draft",
        banner_type: bannerDetails.banner_type || "banners",
        link_url: bannerDetails.link_url || "",
        video_url: bannerDetails.video_url || "",
        sort_order: bannerDetails.sort_order ?? null,
        active_date: bannerDetails.active_date || null,
        expire_date: bannerDetails.expire_date || null,
        banner_image: null,
        newsId: bannerDetails.news_id ?? null,
        is_default: !!bannerDetails.is_default,
        targeting: targetingFromResponse(bannerDetails.targeting),
      });
    }
  }, [bannerDetails, reset]);

  useEffect(() => {
    if (bannerImageMedia) setValue("banner_image", bannerImageMedia);
  }, [bannerImageMedia, setValue]);

  const title = watch("title");
  const bannerImage = watch("banner_image");
  const bannerType = watch("banner_type");
  const requiredAspectRatio = bannerTypeAspectRatio(bannerType);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = (values: BannerFormValues) => {
    setIsSubmitting(true);
    const payload = {
      title: values.title,
      slug: slugify(values.title),
      // No Content field in this form; the backend still requires the key.
      content: "",
      status: values.status,
      link_url: values.link_url || null,
      video_url: values.video_url || null,
      sort_order: values.sort_order ?? 0,
      is_active: true,
      active_date: values.active_date || null,
      expire_date: values.expire_date || null,
      published_at: null,
      banner_image_id: values.banner_image?.id,
      banner_type: values.banner_type,
      news_id: values.newsId || null,
      is_default: values.is_default,
      targeting: buildTargetingPayload(values.targeting),
      ...(isEdit ? {} : { orgId: selectedOrgId || undefined }),
    };

    const request = isEdit ? updateBanner(id!, payload) : createBanner(payload);
    request
      .then(() => {
        showSnackbar(isEdit ? "Banner updated successfully" : "Banner added successfully");
        queryClient.invalidateQueries({ queryKey: ["cms-banners"] });
        // The list query above doesn't cover this item's own details cache -
        // without this, re-opening Edit within the 5-minute default
        // staleTime serves the stale pre-update data until a hard refresh.
        queryClient.invalidateQueries({ queryKey: ["cms-banner-details", id] });
        navigate(cms_banners);
      })
      .catch((e) => {
        showSnackbar(e?.response?.data?.error?.message || "Something went wrong");
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <MainLayout>
      <Card sx={{ width: "100%", borderRadius: 4, m: 1 }} elevation={1}>
        <CardContent>
          <Box display="flex" flexDirection="row" alignItems="center">
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" fontWeight="600" ml={1}>
              {viewMode ? "View Banner" : isEdit ? "Edit Banner" : "Add Banner"}
            </Typography>
          </Box>
          <Divider sx={{ my: 3, opacity: 0.5, borderBottom: "1px dotted #7C797C" }} />

          {isEdit && isBannerLoading ? (
            <Loading label="Loading banner..." />
          ) : (
          <>
          {!isEdit && !viewMode && (
            <Box maxWidth={360}>
              <OrgSelector label="Organisation" fullWidth lockToOwnOrg platformDefaultsToOwnOrg />
            </Box>
          )}

          <SectionHeader mt={!isEdit && !viewMode ? 3 : 0}>Basic Details</SectionHeader>

          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                required
                label={<>Title <span style={{ color: "red" }}>*</span></>}
                margin="normal"
                disabled={viewMode}
              />
            )}
          />

          <Box display="flex" flexDirection="row" gap={4}>
            <Controller
              name="banner_type"
              control={control}
              render={({ field }) => (
                <TextField {...field} select fullWidth label="Banner Type" margin="normal" disabled={viewMode}>
                  {BANNER_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label} ({option.aspectRatio})
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <TextField {...field} select fullWidth label="Status" margin="normal" disabled={viewMode}>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                </TextField>
              )}
            />
          </Box>

          <SectionHeader>Banner Media</SectionHeader>

          {requiredAspectRatio && (
            <Typography variant="body2" color="text.secondary" mb={2}>
              {bannerTypeLabel(bannerType)} banners require a <b>{requiredAspectRatio}</b> aspect ratio image.
            </Typography>
          )}
          <Box display="flex" flexDirection="row" flexWrap="wrap" gap={4}>
            <Controller
              name="banner_image"
              control={control}
              render={({ field }) => (
                <MediaPicker
                  label="Banner Image"
                  required
                  value={field.value}
                  disabled={viewMode}
                  onChange={field.onChange}
                  recommendedAspectRatio={requiredAspectRatio}
                />
              )}
            />
          </Box>

          <Box mt={3}>
            <Controller
              name="video_url"
              control={control}
              render={({ field }) => (
                <VideoPicker
                  label="Video"
                  value={field.value}
                  disabled={viewMode}
                  onChange={field.onChange}
                  recommendedAspectRatio={requiredAspectRatio}
                />
              )}
            />
          </Box>

          <SectionHeader>Link & Display Order</SectionHeader>

          <Box display="flex" flexDirection="row" gap={4}>
            <Controller
              name="link_url"
              control={control}
              render={({ field }) => (
                <TextField {...field} fullWidth label="Link URL" margin="normal" disabled={viewMode} />
              )}
            />
            <Controller
              name="sort_order"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ""}
                  onChange={(e) => {
                    const parsed = e.target.value ? Number(e.target.value) : null;
                    field.onChange(parsed !== null && parsed < 0 ? 0 : parsed);
                  }}
                  type="number"
                  inputProps={{ min: 0 }}
                  fullWidth
                  label="Sort Order"
                  margin="normal"
                  disabled={viewMode}
                />
              )}
            />
          </Box>

          <SectionHeader>Scheduling</SectionHeader>

          <Box display="flex" flexDirection="row" gap={4}>
            <Controller
              name="active_date"
              control={control}
              render={({ field }) => (
                <DateTimePicker
                  label="Active From"
                  readOnly={viewMode}
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(value: Dayjs | null) => field.onChange(value ? value.toISOString() : null)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              )}
            />
            <Controller
              name="expire_date"
              control={control}
              render={({ field }) => (
                <DateTimePicker
                  label="Expires On"
                  readOnly={viewMode}
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(value: Dayjs | null) => field.onChange(value ? value.toISOString() : null)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              )}
            />
          </Box>

          <SectionHeader>Related Content & Audience</SectionHeader>

          <Controller
            name="newsId"
            control={control}
            render={({ field }) => (
              <Autocomplete
                fullWidth
                disabled={viewMode}
                options={newsOptions || []}
                getOptionLabel={(option) => option?.title || ""}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                value={newsOptions?.find((option) => option.id === field.value) || null}
                onChange={(_, newValue) => field.onChange(newValue?.id ?? null)}
                renderInput={(params) => <TextField {...params} label="News" />}
              />
            )}
          />

          <Box mt={3}>
            <TargetingFields control={control} setValue={setValue} viewMode={viewMode} />
          </Box>

          <SectionHeader>Additional Settings</SectionHeader>

          <Controller
            name="is_default"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox checked={!!field.value} disabled={viewMode} onChange={(e) => field.onChange(e.target.checked)} />}
                label="Default Banner"
              />
            )}
          />

          <Box display="flex" flexDirection="row" justifyContent="space-between" mt={5}>
            <Button onClick={() => navigate(-1)} variant="outlined" sx={{ color: black }} disabled={isSubmitting}>
              {viewMode ? "Back" : "Cancel"}
            </Button>
            {!viewMode && (
              <Button
                onClick={handleSubmit(onSubmit)}
                disabled={isSubmitting || !title || !bannerImage}
                variant="contained"
                startIcon={isSubmitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : undefined}
              >
                {isSubmitting ? (isEdit ? "Updating..." : "Adding...") : isEdit ? "Update Banner" : "Add Banner"}
              </Button>
            )}
          </Box>
          </>
          )}
        </CardContent>
      </Card>
    </MainLayout>
  );
}
